import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc, updateDoc, query, where, getDocs, collection, getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Share2, TrendingUp, Wallet, Gift, User, Home, LogOut, Menu, X } from "lucide-react";
import { auth, db } from "../FirebaseConfig";


const generateReferralCode = (name = "user") => {
  return name.toUpperCase().slice(0, 4) + Math.floor(1000 + Math.random() * 9000);
};

const ReferralSystem = () => {
  const [followed, setFollowed] = useState({
    instagram: false,
    twitter: false,
    tiktok: false,
    whatsapp: false,
  });

  const [referralData, setReferralData] = useState({
    referralCode: null,
    referralCount: 0,
    totalEarnings: 0,
    sellerReferrals: 0,
    buyerReferrals: 0,
    withdrawalHistory: [],
  });

  const [loading, setLoading] = useState(false);
  const [referralAvailable, setReferralAvailable] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [bankDetails, setBankDetails] = useState({ bankName: "", accountNumber: "", accountName: "" });
  const [withdrawalAmount, setWithdrawalAmount] = useState("");
  const [showBankDetailsPopup, setShowBankDetailsPopup] = useState(false);
  const [bankDetailsSubmitted, setBankDetailsSubmitted] = useState(false);

  useEffect(() => {
    const today = new Date();
    const unlockDate = new Date("2025-07-21");
    const endDate = new Date("2026-10-06");
    if (today >= unlockDate && today < endDate) {
      setReferralAvailable(true);
    }
  }, []);

  const allFollowed = Object.values(followed).every(Boolean);
  const canWithdraw = referralData.totalEarnings >= 1600;
  const earnedAmount = referralData.totalEarnings;

  const loadReferralData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      const profileRef = doc(db, "profiles", user.uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        setReferralData({
          referralCode: data.referralCode || null,
          referralCount: (data.sellerReferrals || 0) + (data.buyerReferrals || 0),
          totalEarnings: data.totalEarnings || 0,
          sellerReferrals: data.sellerReferrals || 0,
          buyerReferrals: data.buyerReferrals || 0,
          withdrawalHistory: data.withdrawalHistory || [],
        });

        // Pre-fill bank details if available
        if (data.bankName && data.accountNumber && data.accountName) {
          setBankDetails({
            bankName: data.bankName,
            accountNumber: data.accountNumber,
            accountName: data.accountName,
          });
          setBankDetailsSubmitted(true);
        }
      }
    } catch (err) {
      console.error("Error loading referral data:", err);
    }
  };

  useEffect(() => {
    loadReferralData();
  }, []);

  const handleFollow = (platform) => {
    setFollowed((prev) => ({ ...prev, [platform]: true }));
    const links = {
      instagram: "https://www.instagram.com/hello.ibuyunn?igsh=eDRtcnVpcmNyOTNs",
      twitter: "https://x.com/ucartstore?s=21",
      tiktok: "https://www.tiktok.com/@ucart.store?_r=1&_t=ZS-96GNFfvFrrb",
      whatsapp: "https://chat.whatsapp.com/LmcchD8QjgBGiwHe1sNgk7?mode=ac_t",
    };
    window.open(links[platform], "_blank");
  };

  const handleJoinReferral = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        toast.error("You must be logged in");
        setLoading(false);
        return;
      }

      // ✅ FIX: VALIDATE that all platforms are followed
      if (!allFollowed) {
        toast.error("Please follow all 4 social media platforms first");
        setLoading(false);
        return;
      }

      const profileRef = doc(db, "profiles", user.uid);
      const profileSnap = await getDoc(profileRef);

      if (profileSnap.exists()) {
        const data = profileSnap.data();
        if (data.referralCode) {
          setReferralData((prev) => ({ ...prev, referralCode: data.referralCode }));
          toast.info("You already joined. Referral code loaded.");
          setLoading(false);
          return;
        }
      }

      const code = generateReferralCode(user.displayName || "user");

      await setDoc(
        profileRef,
        {
          referralCode: code,
          referralCount: 0,
          totalEarnings: 0,
          sellerReferrals: 0,
          buyerReferrals: 0,
          withdrawalHistory: [],
        },
        { merge: true }
      );

      setReferralData((prev) => ({ ...prev, referralCode: code }));
      toast.success(`Referral Program Activated! Your code is ${code}`);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (referralData.referralCode) {
      navigator.clipboard.writeText(referralData.referralCode);
      toast.success("Referral code copied to clipboard!");
    }
  };

  const getReferralLink = () => {
    if (!referralData.referralCode) return "";
    const baseUrl = window.location.origin;
    return `${baseUrl}/Login?ref=${referralData.referralCode}`;
  };

  const copyReferralLink = () => {
    const link = getReferralLink();
    if (link) {
      navigator.clipboard.writeText(link);
      toast.success("Referral link copied!");
    }
  };

  const handleWithdrawal = async () => {
    if (!bankDetailsSubmitted) {
      toast.error("Please submit your bank details first.");
      return;
    }

    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      toast.error("Enter a valid withdrawal amount.");
      return;
    }

    if (parseFloat(withdrawalAmount) > earnedAmount) {
      toast.error("Withdrawal amount exceeds your earnings.");
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("You must be logged in.");
        return;
      }

      // Send bank details to WhatsApp
      const message = `
🏦 WITHDRAWAL REQUEST - uCart Referral Program

👤 User: ${user.displayName || user.email}
📱 Email: ${user.email}
💰 Withdrawal Amount: ₦${withdrawalAmount}
🏦 Bank Name: ${bankDetails.bankName}
📌 Account Number: ${bankDetails.accountNumber}
👤 Account Name: ${bankDetails.accountName}

Please process this withdrawal request.
      `.trim();

      const encodedMessage = encodeURIComponent(message);
      const whatsappLink = `https://wa.me/2349017177993?text=${encodedMessage}`;

      window.open(whatsappLink, "_blank");

      // Update withdrawal history
      const profileRef = doc(db, "profiles", user.uid);
      const withdrawal = {
        amount: parseFloat(withdrawalAmount),
        timestamp: new Date(),
        status: "pending",
      };

      await updateDoc(profileRef, {
        totalEarnings: earnedAmount - parseFloat(withdrawalAmount),
        withdrawalHistory: [...(referralData.withdrawalHistory || []), withdrawal],
      });

      toast.success("Withdrawal request sent! Check WhatsApp for confirmation.");
      setWithdrawalAmount("");
      setShowWithdrawModal(false);

      // Reload data
      await loadReferralData();
    } catch (err) {
      console.error(err);
      toast.error("Withdrawal failed. Try again.");
    }
  };

  const handleBankDetailsSubmit = async (e) => {
    e.preventDefault();

    if (!bankDetails.bankName || !bankDetails.accountNumber || !bankDetails.accountName) {
      toast.error("Please fill all bank details.");
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error("You must be logged in.");
        return;
      }

      const profileRef = doc(db, "profiles", user.uid);
      await updateDoc(profileRef, {
        bankName: bankDetails.bankName,
        accountNumber: bankDetails.accountNumber,
        accountName: bankDetails.accountName,
      });

      setBankDetailsSubmitted(true);
      toast.success("Bank details saved successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save bank details.");
    }
  };

  return referralAvailable ? (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-950 text-white">
      {/* Background pattern */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Main Dashboard */}
          {referralData.referralCode ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ staggerChildren: 0.1 }}
              className="space-y-8"
            >
              {/* Top Stats Cards */}
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
                variants={{
                  hidden: { opacity: 0 },
                  show: {
                    opacity: 1,
                    transition: { staggerChildren: 0.1 },
                  },
                }}
                initial="hidden"
                animate="show"
              >
                {/* Earnings Card */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-slate-900/80 backdrop-blur border border-emerald-500/30 rounded-2xl p-6 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-3xl"></div>
                    <div className="relative flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider">Total Earnings</h3>
                      <Wallet className="w-6 h-6 text-emerald-500" />
                    </div>
                    <p className="text-4xl font-bold text-white">₦{earnedAmount.toLocaleString()}</p>
                    <p className="text-sm text-slate-400 mt-2">
                      {canWithdraw ? (
                        <span className="text-emerald-400 font-semibold">✓ Ready to withdraw</span>
                      ) : (
                        <span>₦{(1600 - earnedAmount).toLocaleString()} until withdrawal</span>
                      )}
                    </p>
                  </div>
                </motion.div>

                {/* Referral Count Card */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-slate-900/80 backdrop-blur border border-blue-500/30 rounded-2xl p-6 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 rounded-full blur-3xl"></div>
                    <div className="relative flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-blue-400 uppercase tracking-wider">Total Referrals</h3>
                      <TrendingUp className="w-6 h-6 text-blue-500" />
                    </div>
                    <p className="text-4xl font-bold text-white">{referralData.referralCount}</p>
                    <p className="text-sm text-slate-400 mt-2">Successful referrals</p>
                  </div>
                </motion.div>

                {/* Seller Referrals Card */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600 to-orange-600 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-slate-900/80 backdrop-blur border border-amber-500/30 rounded-2xl p-6 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <h3 className="text-sm font-semibold text-amber-400 uppercase tracking-wider mb-4">Seller Referrals</h3>
                      <p className="text-3xl font-bold text-white">{referralData.sellerReferrals}</p>
                      <p className="text-sm text-slate-400 mt-2">₦200 per seller</p>
                    </div>
                  </div>
                </motion.div>

                {/* Buyer Referrals Card */}
                <motion.div
                  variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } }}
                  className="relative group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative bg-slate-900/80 backdrop-blur border border-purple-500/30 rounded-2xl p-6 overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/5 rounded-full blur-3xl"></div>
                    <div className="relative">
                      <h3 className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-4">Buyer Referrals</h3>
                      <p className="text-3xl font-bold text-white">{referralData.buyerReferrals}</p>
                      <p className="text-sm text-slate-400 mt-2">₦300 per buyer</p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>

              {/* Referral Code & Link Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 overflow-hidden relative"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl"></div>

                <div className="relative z-10 space-y-6">
                  <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Share2 className="w-6 h-6 text-emerald-400" />
                    Share Your Referral Code
                  </h2>

                  {/* Referral Code */}
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-emerald-300 uppercase tracking-wider">Your Referral Code</label>
                    <div className="flex gap-3">
                      <div className="flex-1 bg-slate-800/50 border border-emerald-500/50 rounded-xl px-6 py-4 flex items-center justify-between group cursor-pointer hover:bg-slate-800 transition-all">
                        <span className="text-2xl font-bold text-emerald-400 font-mono tracking-widest">
                          {referralData.referralCode}
                        </span>
                        <Copy
                          className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={copyToClipboard}
                        />
                      </div>
                      <button
                        onClick={copyToClipboard}
                        className="px-6 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-semibold text-white transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-emerald-500/50"
                      >
                        <Copy className="w-5 h-5" />
                        Copy
                      </button>
                    </div>
                  </div>

                  {/* Referral Link */}
                  <div className="space-y-3 pt-4 border-t border-slate-700">
                    <label className="text-sm font-semibold text-emerald-300 uppercase tracking-wider">Referral Link</label>
                    <div className="flex gap-3">
                      <div className="flex-1 bg-slate-800/50 border border-emerald-500/50 rounded-xl px-6 py-4 flex items-center justify-between group cursor-pointer hover:bg-slate-800 transition-all overflow-hidden">
                        <span className="text-sm text-emerald-400 font-mono truncate">
                          {getReferralLink()}
                        </span>
                        <Copy
                          className="w-5 h-5 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={copyReferralLink}
                        />
                      </div>
                      <button
                        onClick={copyReferralLink}
                        className="px-6 py-4 bg-emerald-600 hover:bg-emerald-700 rounded-xl font-semibold text-white transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-emerald-500/50"
                      >
                        <Share2 className="w-5 h-5" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Bank Details & Withdrawal Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-6"
              >
                {/* Bank Details Form */}
                <div className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl"></div>

                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <User className="w-6 h-6 text-blue-400" />
                      Bank Details
                    </h3>

                    {bankDetailsSubmitted ? (
                      <div className="space-y-4">
                        <div className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4">
                          <p className="text-xs text-blue-300 uppercase tracking-wider mb-2">Bank Name</p>
                          <p className="text-lg font-semibold text-white">{bankDetails.bankName}</p>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4">
                          <p className="text-xs text-blue-300 uppercase tracking-wider mb-2">Account Name</p>
                          <p className="text-lg font-semibold text-white">{bankDetails.accountName}</p>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/50 rounded-xl p-4">
                          <p className="text-xs text-blue-300 uppercase tracking-wider mb-2">Account Number</p>
                          <p className="text-lg font-semibold text-white font-mono">****{bankDetails.accountNumber.slice(-4)}</p>
                        </div>
                        <button
                          onClick={() => setBankDetailsSubmitted(false)}
                          className="w-full mt-4 px-4 py-2 border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all"
                        >
                          Edit Details
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleBankDetailsSubmit} className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-slate-300 mb-2">Bank Name</label>
                          <input
                            type="text"
                            value={bankDetails.bankName}
                            onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                            placeholder="e.g., Access Bank, GTBank"
                            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-300 mb-2">Account Name</label>
                          <input
                            type="text"
                            value={bankDetails.accountName}
                            onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                            placeholder="Your account name"
                            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-slate-300 mb-2">Account Number</label>
                          <input
                            type="text"
                            value={bankDetails.accountNumber}
                            onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                            placeholder="Your account number"
                            className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <button
                          type="submit"
                          className="w-full mt-6 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg font-semibold text-white transition-all duration-300 shadow-lg"
                        >
                          Save Bank Details
                        </button>
                      </form>
                    )}
                  </div>
                </div>

                {/* Withdrawal Card */}
                <div className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 overflow-hidden relative">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"></div>

                  <div className="relative z-10">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                      <Wallet className="w-6 h-6 text-emerald-400" />
                      Withdraw Earnings
                    </h3>

                    <div className="space-y-6">
                      <div className="bg-emerald-500/10 border border-emerald-500/50 rounded-xl p-4">
                        <p className="text-xs text-emerald-300 uppercase tracking-wider mb-2">Available Balance</p>
                        <p className="text-3xl font-bold text-emerald-400">₦{earnedAmount.toLocaleString()}</p>
                      </div>

                      <div className="bg-slate-800/50 rounded-xl p-4">
                        <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Minimum Withdrawal</p>
                        <p className="text-lg font-semibold text-white">₦1,600</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {canWithdraw
                            ? "✓ You can withdraw now!"
                            : `${1600 - earnedAmount} more needed`}
                        </p>
                      </div>

                      <button
                        onClick={() => setShowWithdrawModal(true)}
                        disabled={!canWithdraw || !bankDetailsSubmitted}
                        className={`w-full py-3 rounded-lg font-bold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg ${
                          canWithdraw && bankDetailsSubmitted
                            ? "bg-emerald-600 hover:bg-emerald-700 text-white hover:shadow-emerald-500/50 cursor-pointer"
                            : "bg-slate-700 text-slate-400 cursor-not-allowed opacity-50"
                        }`}
                      >
                        <Wallet className="w-5 h-5" />
                        {canWithdraw ? "Withdraw Now" : "Not Eligible Yet"}
                      </button>

                      <p className="text-xs text-slate-400 text-center">
                        {!bankDetailsSubmitted && "Save bank details first"}
                        {bankDetailsSubmitted && !canWithdraw && `Earn ₦${1600 - earnedAmount} more`}
                        {canWithdraw && bankDetailsSubmitted && "Ready for withdrawal"}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* How It Works */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 overflow-hidden relative"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>

                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-white mb-8">How It Works</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                      {
                        title: "Share Your Code",
                        description: "Share your unique referral code or link with friends",
                        icon: "🔗",
                      },
                      {
                        title: "They Sign Up",
                        description: "They create an account using your referral code",
                        icon: "👤",
                      },
                      {
                        title: "You Earn",
                        description: "Seller list: ₦200 | Buyer purchase: ₦300",
                        icon: "💰",
                      },
                    ].map((step, i) => (
                      <div key={i} className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center border border-emerald-500/50">
                          <span className="text-3xl">{step.icon}</span>
                        </div>
                        <h4 className="text-lg font-bold text-white">{step.title}</h4>
                        <p className="text-slate-300">{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Withdrawal History */}
              {referralData.withdrawalHistory && referralData.withdrawalHistory.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 overflow-hidden relative"
                >
                  <div className="absolute top-0 right-0 w-64 h-64 bg-slate-500/5 rounded-full blur-3xl"></div>

                  <div className="relative z-10">
                    <h3 className="text-2xl font-bold text-white mb-6">Withdrawal History</h3>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {referralData.withdrawalHistory.map((withdrawal, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-lg p-4"
                        >
                          <div>
                            <p className="font-semibold text-white">₦{withdrawal.amount.toLocaleString()}</p>
                            <p className="text-sm text-slate-400">
                              {new Date(withdrawal.timestamp).toLocaleDateString()}
                            </p>
                          </div>
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              withdrawal.status === "completed"
                                ? "bg-emerald-500/20 text-emerald-400"
                                : "bg-yellow-500/20 text-yellow-400"
                            }`}
                          >
                            {withdrawal.status.charAt(0).toUpperCase() + withdrawal.status.slice(1)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>
          ) : (
            // ✅ FIX: Join Program Screen - won't show referral code until all followed
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-2xl mx-auto"
            >
              <div className="bg-slate-900/60 backdrop-blur border border-emerald-500/30 rounded-2xl p-8 sm:p-12 overflow-hidden relative text-center">
                <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 space-y-8">
                  <div>
                    <h2 className="text-4xl font-bold text-white mb-3">Join the Referral giveaway</h2>
                    <p className="text-xl text-slate-300 mb-2">Earn money by referring your friends</p>
                  </div>
                  <div className="space-y-4">
                    <p className="text-slate-300">Follow us on all platforms to unlock your referral code:</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {[
                        { platform: "instagram", label: "Follow Instagram" },
                        { platform: "twitter", label: "Follow Twitter" },
                        { platform: "tiktok", label: "Follow TikTok" },
                        { platform: "whatsapp", label: "Join WhatsApp" },
                      ].map((item) => (
                        <motion.button
                          key={item.platform}
                          onClick={() => handleFollow(item.platform)}
                          disabled={followed[item.platform]}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`py-3 rounded-lg font-bold transition-all duration-300 ${
                            followed[item.platform]
                              ? "bg-emerald-600 text-white"
                              : "bg-slate-800 border border-slate-700 text-slate-300 hover:border-slate-600"
                          }`}
                        >
                          {followed[item.platform] ? `✓ ${item.label}` : item.label}
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Progress indicator */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <p className="text-sm text-slate-400 mb-2">
                      Followed: {Object.values(followed).filter(Boolean).length} / 4
                    </p>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <motion.div
                        className="bg-emerald-500 h-2 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${(Object.values(followed).filter(Boolean).length / 4) * 100}%` }}
                        transition={{ duration: 0.3 }}
                      ></motion.div>
                    </div>
                  </div>

                  <motion.button
                    onClick={handleJoinReferral}
                    disabled={!allFollowed || loading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg ${
                      allFollowed && !loading
                        ? "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-emerald-500/50"
                        : "bg-slate-700 text-slate-400 cursor-not-allowed opacity-50"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Activating...</span>
                      </>
                    ) : (
                      <>
                        <Gift className="w-6 h-6" />
                        <span>Get Your Referral Code</span>
                      </>
                    )}
                  </motion.button>

                  <p className="text-sm text-slate-400">
                    {allFollowed
                      ? "✓ All platforms followed! Click the button above to get your code."
                      : `Follow all 4 platforms to activate your unique referral code`}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </main>

        {/* Withdrawal Modal */}
        <AnimatePresence>
          {showWithdrawModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
              onClick={() => setShowWithdrawModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-slate-900 border border-emerald-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl"
              >
                <h3 className="text-2xl font-bold text-white mb-4">Request Withdrawal</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">
                      Withdrawal Amount
                    </label>
                    <div className="relative">
                      <span className="absolute left-4 top-3 text-xl text-emerald-400">₦</span>
                      <input
                        type="number"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        placeholder="Enter amount"
                        max={earnedAmount}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-lg px-4 py-3 pl-8 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-2">
                      Available: ₦{earnedAmount.toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowWithdrawModal(false)}
                    className="flex-1 px-4 py-3 border border-slate-700 rounded-lg text-white hover:bg-slate-800 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWithdrawal}
                    className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-semibold transition-all shadow-lg"
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  ) : (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 to-slate-900 px-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        <div className="bg-slate-900 border border-slate-700 p-8 rounded-2xl">
          <Gift className="w-16 h-16 text-slate-500 mx-auto mb-4" />
          <p className="text-xl font-bold text-white mb-2">Program Coming Soon</p>
          <p className="text-slate-400">
            The referral program will be unlocked on July 21st, 2025, and will run until December 1st, 2025.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block px-6 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-semibold transition-all"
          >
            Go Home
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default ReferralSystem;